/**
 * Keyboard Shortcuts Registry
 * 
 * Centralized registry for managing keyboard shortcuts throughout the application.
 * Supports WCAG 2.0 Level A keyboard navigation requirements.
 */

class KeyboardShortcutRegistry {
  constructor() {
    this.shortcuts = new Map();
    this.enabled = true;
    this.listener = null;
  }

  /**
   * Initialize the keyboard shortcut listener
   */
  init() {
    if (this.listener) return;

    this.listener = (event) => {
      if (!this.enabled) return;

      // Don't trigger shortcuts when typing in inputs
      const target = event.target;
      const tagName = target.tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || target.isContentEditable) {
        return;
      }

      const key = this.getKeyString(event);
      const handler = this.shortcuts.get(key);

      if (handler) {
        event.preventDefault();
        handler(event);
      }
    };

    document.addEventListener('keydown', this.listener);
  }

  /**
   * Get a string representation of the key combination
   * @param {KeyboardEvent} event - Keyboard event
   * @returns {string} Key string
   */
  getKeyString(event) {
    const parts = [];

    if (event.ctrlKey) parts.push('ctrl');
    if (event.altKey) parts.push('alt');
    if (event.shiftKey) parts.push('shift');
    if (event.metaKey) parts.push('meta');

    const key = event.key.toLowerCase();
    parts.push(key);

    return parts.join('+');
  }

  /**
   * Register a keyboard shortcut
   * @param {string} keys - Key combination (e.g., 'ctrl+k', 'alt+s')
   * @param {Function} handler - Handler function
   * @param {string} description - Description of the shortcut
   */
  register(keys, handler, description = '') {
    const normalizedKeys = keys.toLowerCase().replace(/\s/g, '');
    this.shortcuts.set(normalizedKeys, handler);

    // Store description for help dialog
    if (description) {
      handler._description = description;
      handler._keys = keys;
    }
  }

  /**
   * Unregister a keyboard shortcut
   * @param {string} keys - Key combination
   */
  unregister(keys) {
    const normalizedKeys = keys.toLowerCase().replace(/\s/g, '');
    this.shortcuts.delete(normalizedKeys);
  }

  /**
   * Enable keyboard shortcuts
   */
  enable() {
    this.enabled = true;
  }

  /**
   * Disable keyboard shortcuts temporarily
   */
  disable() {
    this.enabled = false;
  }

  /**
   * Get all registered shortcuts with descriptions
   * @returns {Array} Array of shortcut info objects
   */
  getAllShortcuts() {
    const shortcuts = [];
    this.shortcuts.forEach((handler, keys) => {
      if (handler._description) {
        shortcuts.push({
          keys: handler._keys || keys,
          description: handler._description
        });
      }
    });
    return shortcuts;
  }

  /**
   * Destroy the keyboard shortcut listener
   */
  destroy() {
    if (this.listener) {
      document.removeEventListener('keydown', this.listener);
      this.listener = null;
    }
    this.shortcuts.clear();
  }
}

// Create singleton instance
const registry = new KeyboardShortcutRegistry();

// Common keyboard shortcuts
export const SHORTCUTS = {
  SEARCH: 'ctrl+k',
  HELP: 'ctrl+/',
  CLOSE: 'escape',
  SUBMIT: 'ctrl+enter',
  CANCEL: 'escape',
  NEXT: 'ctrl+arrowright',
  PREVIOUS: 'ctrl+arrowleft',
  FIRST: 'ctrl+home',
  LAST: 'ctrl+end',
};

// Vue plugin
const KeyboardShortcutsPlugin = {
  install(Vue, options = {}) {
    registry.init();

    // Add to Vue prototype
    Vue.prototype.$shortcuts = registry;
    Vue.prototype.$registerShortcut = (keys, handler, description) => {
      registry.register(keys, handler, description);
    };
    Vue.prototype.$unregisterShortcut = (keys) => {
      registry.unregister(keys);
    };

    // Register default shortcuts if provided
    if (options.shortcuts) {
      Object.entries(options.shortcuts).forEach(([keys, config]) => {
        registry.register(keys, config.handler, config.description);
      });
    }
  }
};

/**
 * Vue directive for keyboard shortcuts
 * Usage: v-shortcut="{ keys: 'ctrl+k', handler: handleSearch }"
 */
export const ShortcutDirective = {
  bind(el, binding) {
    const { keys, handler, description } = binding.value;

    if (!keys || !handler) {
      console.warn('v-shortcut requires keys and handler');
      return;
    }

    // Store the keys on the element for cleanup
    el._shortcutKeys = keys;

    // Register the shortcut
    registry.register(keys, handler, description);
  },

  unbind(el) {
    if (el._shortcutKeys) {
      registry.unregister(el._shortcutKeys);
      el._shortcutKeys = null;
    }
  }
};

/**
 * Mixin for components that use keyboard shortcuts
 */
export const KeyboardShortcutMixin = {
  data() {
    return {
      registeredShortcuts: []
    };
  },

  methods: {
    registerShortcut(keys, handler, description) {
      registry.register(keys, handler, description);
      this.registeredShortcuts.push(keys);
    },

    unregisterShortcut(keys) {
      registry.unregister(keys);
      this.registeredShortcuts = this.registeredShortcuts.filter(k => k !== keys);
    }
  },

  beforeDestroy() {
    // Clean up all shortcuts registered by this component
    this.registeredShortcuts.forEach(keys => {
      registry.unregister(keys);
    });
  }
};

// Export both the plugin and the registry
export default KeyboardShortcutsPlugin;
export { registry };
